import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { RelationalOperator } from '@mtna/data-core-ts';

import { RdsChip } from '../chips/index';
import { RDS_NUMERIC_OPERATION_OPTIONS, RdsNumericFilter, RdsNumericOperation, RdsNumericOperationOption } from './numeric-filter.model';

const DEFAULT_SUPPORTED_NUMERIC_FILTER_OPERATIONS: RdsNumericOperation[] = ['GT', 'GE', 'EQ', 'NE', 'LT', 'LE'];

@Component({
  selector: 'rds-c-numeric-filter',
  templateUrl: './numeric-filter.component.html',
  styleUrls: ['./numeric-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsNumericFilterComponent implements OnChanges {
  static ngAcceptInputType_supportedOperators: RelationalOperator[] | null | undefined;

  /** Numeric Filters */
  @Input() filters: Array<RdsNumericFilter> | null | undefined = [];

  /** Whether to use the large chip style variant */
  @HostBinding('class.large-chips')
  @Input()
  largeChips = false;

  /**
   * The supported `RelationalOperator`s. i.e. what operaters does the current data product support.
   * Defaults to 'GT', 'GE', 'EQ', 'NE', 'LT', 'LE'.
   */
  @Input()
  set supportedOperators(operators: RelationalOperator[]) {
    this._supportedOperators = operators || DEFAULT_SUPPORTED_NUMERIC_FILTER_OPERATIONS;
    this.determineSelectOptions();
    this.cdr.markForCheck();
  }
  get supportedOperators(): RelationalOperator[] {
    return this._supportedOperators;
  }
  private _supportedOperators: RelationalOperator[] = DEFAULT_SUPPORTED_NUMERIC_FILTER_OPERATIONS;

  /** Event emitted when the user adds new filter */
  @Output() add = new EventEmitter<RdsNumericFilter>();
  /** Event emitted when the user removes a filter */
  @Output() remove = new EventEmitter<RdsNumericFilter>();

  /** A record to indicate which options should be enabled based on the current selected filters */
  enabledOptions: Record<RdsNumericOperation, boolean> = { GT: true, GE: true, EQ: true, NE: true, LT: true, LE: true };

  /** Select options to choose what type of filter operator to use. */
  filterOptions: ReadonlyArray<RdsNumericOperationOption> = [];

  /** Added filter operation and value from inputs */
  newValue = new RdsNumericFilter(null, null);

  constructor(private cdr: ChangeDetectorRef) {
    // Set initial, default options
    this.determineSelectOptions();
  }

  /** Given the current supported operations, what select options should be visible. */
  determineSelectOptions() {
    let newOptions: RdsNumericOperationOption[] = [];
    this.supportedOperators.forEach((operator) => {
      const option: RdsNumericOperationOption | undefined = RDS_NUMERIC_OPERATION_OPTIONS.find((o) => o.type === operator);
      if (option) {
        newOptions = [...newOptions, option];
      }
    });
    this.filterOptions = newOptions;
  }

  /**
   * Emit added values and clear the inputs
   */
  handleAdd(newValue: RdsNumericFilter) {
    this.add.emit(newValue);
    this.newValue = new RdsNumericFilter(null, null);
  }

  handleRemove(chip: RdsChip) {
    if (this.filters) {
      const found = this.filters.find((f) => (!f.operation ? false : f.operation.type + f.value === chip.id));
      this.remove.emit(found);
    }
  }

  /**
   * Any time the filters change, determine which operations are still applicable, if any
   */
  ngOnChanges(changes: SimpleChanges) {
    const filtersChange: SimpleChange = changes.filters;
    if (filtersChange) {
      const changedFilters: Array<RdsNumericFilter> | null | undefined = filtersChange.currentValue;
      if (changedFilters) {
        // start all as true and flip to false
        this.enabledOptions = { GT: true, GE: true, EQ: true, NE: true, LT: true, LE: true };
        for (const f of changedFilters) {
          switch (f.operation?.type) {
            case 'GT':
            case 'GE':
              this.enabledOptions.GT = false;
              this.enabledOptions.GE = false;
              break;
            case 'LT':
            case 'LE':
              this.enabledOptions.LT = false;
              this.enabledOptions.LE = false;
              break;
          }
        }
        this.cdr.markForCheck();
      }
    }
  }
}
